import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from './api';
import {
  clearAuthSession,
  getAuthToken,
  getStoredUserJson,
  persistAuthSession,
} from './auth-storage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL (Google OAuth callback)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          const response = await auth.sessionData(sessionId);
          setUser(response.data.user);
          persistAuthSession(response.data.user, response.data.session_token, true);
          // Clean URL
          window.history.replaceState(null, '', window.location.pathname);
          setLoading(false);
          return;
        }
      }

      const storedUser = getStoredUserJson();
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        try {
          const response = await auth.me();
          setUser(response.data);
          const token = getAuthToken();
          const rememberMe = !!localStorage.getItem('token');
          if (token) {
            persistAuthSession(response.data, token, rememberMe);
          }
        } catch {
          clearAuthSession();
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, options = {}) => {
    const rememberMe = options.rememberMe !== false;
    const response = await auth.login({ email, password });
    setUser(response.data.user);
    persistAuthSession(response.data.user, response.data.access_token, rememberMe);
    return response.data;
  };

  const register = async (data) => {
    const response = await auth.register(data);
    // New registration flow - don't log in, return response for redirect to verification page
    return response.data;
  };

  const logout = async () => {
    try {
      await auth.logout();
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
    clearAuthSession();
  };

  const loginWithGoogle = () => {
    const redirectUrl = encodeURIComponent(window.location.origin + '/dashboard');
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
